/**
 * useFeatureFlag — thin wrapper around PostHog's client-side feature flags.
 *
 * Returns `true` when the flag is enabled for the current user, `false`
 * otherwise (including while loading or when PostHog is not initialised).
 */

import { useEffect, useState } from 'react';
import posthog from 'posthog-js';

export function useFeatureFlag(flagKey: string): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => {
    try {
      return posthog.isFeatureEnabled(flagKey) === true;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const refresh = () => {
      setEnabled(posthog.isFeatureEnabled(flagKey) === true);
    };

    // Flags can load between render and this subscription (for example, when a
    // lazy settings tab mounts after the authenticated /flags response). Read
    // once immediately as well as subscribing, otherwise that completed event
    // is missed and a cohort-gated surface remains incorrectly hidden.
    refresh();
    const unsubscribe = posthog.onFeatureFlags(refresh);

    return () => {
      unsubscribe?.();
    };
  }, [flagKey]);

  return enabled;
}
